import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PremiumCombobox } from '@/components/ui/premium-combobox';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TrendingUp, Users, ExternalLink, Video, Link2, Search, Film, CheckCircle2, Clock3, XCircle } from 'lucide-react';
import { logUpdate } from '@/lib/audit-log';
import { DataPagination } from '@/components/DataPagination';

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtUsd(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return `$${Number(n).toLocaleString()}`;
}

function fmtNum(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

// ── Types ─────────────────────────────────────────────────────────────────────

type CampaignMeta = {
  id: string;
  brand_name: string;
  product_name: string;
  min_videos: number | null;
  guaranteed_payment: number | null;
  is_hybrid: boolean | null;
  is_bmc_max: boolean | null;
  max_required_videos: number | null;
};

type HybridProgressVideo = {
  video_id: string;
  video_name: string | null;
  gmv: number;
  video_views: number;
  items_sold: number;
  post_time: string | null;
};

type ProgressRow = {
  id: string;
  campaign_id: string;
  creator_name: string;
  creator_id: string | null;
  video_count: number;
  total_gmv: number;
  total_views: number;
  total_items_sold: number;
  videos: HybridProgressVideo[];
  payment_status: 'unpaid' | 'paid';
  payment_basis: 'manual' | 'tap' | 'bmc_max' | null;
  payment_transaction_id: string | null;
  payment_amount_cents: number | null;
  payment_notes: string | null;
  paid_at: string | null;
  paid_by: string | null;
  campaign: CampaignMeta | null;
};

/** One video entry inside hybrid_video_submissions.videos JSONB array. */
type ManualVideoEntry = {
  video_url: string;
  tiktok_video_id: string | null; // resolved async; used to match against TAP video IDs
  submitted_at: string;
  notes: string | null;
  // Approval fields (set by admin)
  status: 'pending' | 'approved' | 'rejected' | 'deleted';
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  // Soft-delete fields (set by creator)
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_reason?: string | null;
  // Per-video payment tracking
  is_paid: boolean;
  paid_at: string | null;
  paid_by: string | null;
  per_video_amount_cents: number | null;
};

/** One row in hybrid_video_submissions — one per creator per campaign. */
type ManualCreatorRow = {
  id: string;
  campaign_id: string;
  creator_name: string;
  creator_id: string | null;
  videos: ManualVideoEntry[];
  video_count: number;
  approved_count: number;
  last_updated_at: string;
  campaign: CampaignMeta | null;
  submission_type: 'hybrid' | 'bmc_max' | null;
  // Payment fields (added in migration 006)
  payment_status: 'unpaid' | 'paid';
  payment_basis: 'manual' | 'tap' | 'bmc_max' | null;
  payment_transaction_id: string | null;
  payment_amount_cents: number | null;
  payment_notes: string | null;
  paid_at: string | null;
  paid_by: string | null;
};

/** Unified pay source — tracks which tab and which rows are being paid. */
type PaySource =
  | { source: 'tap';    tapRow: ProgressRow;     manualRow?: ManualCreatorRow }
  | { source: 'manual'; manualRow: ManualCreatorRow; tapRow?: ProgressRow };

/** Data loaded into the videos detail modal (on-demand, one creator at a time). */
type VideoModalData = {
  tap_videos: HybridProgressVideo[];
  manual_subs: ManualVideoEntry[];
};

/**
 * Given TAP videos and manually submitted videos, compute the de-duplicated counts.
 * A manual submission is considered "already in TAP" when its tiktok_video_id matches
 * a TAP video_id. Unenriched submissions (no tiktok_video_id) are always counted as unique
 * — we can't know if they overlap until the ID is resolved.
 */
function computeDedupStats(tapVideos: HybridProgressVideo[], manualSubs: ManualVideoEntry[]) {
  const tapIds = new Set(tapVideos.map(v => v.video_id).filter(Boolean));
  const inTap = manualSubs.filter(s => s.tiktok_video_id && tapIds.has(s.tiktok_video_id));
  const manualOnly = manualSubs.filter(s => !s.tiktok_video_id || !tapIds.has(s.tiktok_video_id));
  return {
    tap_count: tapVideos.length,
    manual_only_count: manualOnly.length,
    in_tap_count: inTap.length,
    unique_total: tapVideos.length + manualOnly.length,
    tapIds,
  };
}

/**
 * A video the creator withdrew from the creator app (soft-deleted). Older rows
 * set status 'deleted', newer ones set the is_deleted flag — check both.
 * Withdrawn videos are excluded from every count in this component.
 */
const isWithdrawn = (v: ManualVideoEntry) => v.status === 'deleted' || !!v.is_deleted;

/** Videos still standing in a submission row — i.e. video_count minus withdrawn ones. */
function liveVideoCount(row: { videos?: ManualVideoEntry[] | null }) {
  return (row.videos ?? []).filter(v => !isWithdrawn(v)).length;
}

/** Status sub-tab shown above the Manual Submissions / BMC Max tables. */
type ManualStatusTab = 'all' | 'need_review' | 'in_progress' | 'paid';

/**
 * Badge counts for the four status sub-tabs — { total, need_review, in_progress, paid }.
 * Mirrors the get_hybrid_ledger_status_counts() RPC's return row.
 */
type ManualStatusCounts = { total: number; need_review: number; in_progress: number; paid: number };
const EMPTY_STATUS_COUNTS: ManualStatusCounts = { total: 0, need_review: 0, in_progress: 0, paid: 0 };

/**
 * TikTok's canonical web-format video URL — https://www.tiktok.com/@handle/video/id.
 * Creators can paste a short link (vm.tiktok.com), a mobile link (m.tiktok.com), or
 * any other TikTok share format; the raw submitted URL is kept as-is in `video_url`,
 * but admins reviewing videos need a reliable web link. Reconstructed client-side from
 * data already on hand — the row's creator_name (the creator's TikTok handle) plus the
 * video's resolved tiktok_video_id — same pattern already used for TAP-detected videos
 * (see the "Watch" link in the TAP videos tab). Returns null until tiktok_video_id
 * resolves (same async-enrichment delay as the TAP Match column).
 */
function buildTiktokWebUrl(creatorName: string | null | undefined, tiktokVideoId: string | null | undefined): string | null {
  if (!creatorName || !tiktokVideoId) return null;
  return `https://www.tiktok.com/@${creatorName}/video/${tiktokVideoId}`;
}

// ── Global totals ─────────────────────────────────────────────────────────────

/** PostgREST caps every response at 1000 rows, so a single .select() silently truncates. */
const ROW_PAGE_SIZE = 1000;
/** Backstop against a runaway loop — 100 pages is 100k submission rows. */
const MAX_ROW_PAGES = 100;

/**
 * Runs a query in 1000-row pages until one comes up short, so callers get every
 * matching row instead of PostgREST's silently-truncated first page.
 *
 * `build` must apply a stable tiebreaker order (e.g. `.order('id')` after the
 * sort column) — paging over a non-unique sort lets tied rows shift between
 * requests, which double-counts some and skips others.
 */
async function fetchAllPages<T>(
  build: (from: number, to: number) => PromiseLike<{ data: unknown; error: unknown }>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 0; page < MAX_ROW_PAGES; page++) {
    const from = page * ROW_PAGE_SIZE;
    const { data, error } = await build(from, from + ROW_PAGE_SIZE - 1);
    if (error) throw error;
    const batch = (data ?? []) as T[];
    rows.push(...batch);
    if (batch.length < ROW_PAGE_SIZE) break;
  }
  return rows;
}

/**
 * Every hybrid_video_submissions row's videos array, across ALL submission types.
 *
 * Deliberately unfiltered: no submission_type, no campaign, no creator search.
 * The three summary cards answer "what's in the system overall", so they must
 * cover hybrid, BMC Max, and any row whose type is null or something new.
 */
function fetchAllSubmissionVideos() {
  return fetchAllPages<{ videos: ManualVideoEntry[] | null }>((from, to) =>
    supabase
      .from('hybrid_video_submissions')
      .select('id, videos')
      .order('id', { ascending: true })
      .range(from, to),
  );
}

/**
 * Every hybrid_campaign_progress row's TAP-detected videos, across all campaigns.
 * Folded into the unique total alongside the manual submissions above.
 */
function fetchAllTapVideos() {
  return fetchAllPages<{ videos: HybridProgressVideo[] | null }>((from, to) =>
    supabase
      .from('hybrid_campaign_progress')
      .select('id, videos')
      .order('id', { ascending: true })
      .range(from, to),
  );
}

/** Per-accent styling for the summary cards. */
const STAT_ACCENTS = {
  slate: {
    tile:  'bg-slate-500/10 text-slate-600 dark:text-slate-300',
    value: 'text-foreground',
    bar:   'bg-slate-400 dark:bg-slate-500',
    glow:  'from-slate-500/[0.08]',
  },
  emerald: {
    tile:  'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    value: 'text-emerald-600 dark:text-emerald-400',
    bar:   'bg-emerald-500',
    glow:  'from-emerald-500/[0.10]',
  },
  amber: {
    tile:  'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    value: 'text-amber-600 dark:text-amber-400',
    bar:   'bg-amber-500',
    glow:  'from-amber-500/[0.10]',
  },
  rose: {
    tile:  'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    value: 'text-rose-600 dark:text-rose-400',
    bar:   'bg-rose-500',
    glow:  'from-rose-500/[0.10]',
  },
} as const;

/** One segment of a card's bottom bar — width is a 0–100 percentage. */
type StatSegment = { pct: number; className: string };

function LedgerStatCard({ icon: Icon, label, value, sub, segments, accent, loading }: {
  icon: React.ElementType;
  label: string;
  value: number;
  sub: React.ReactNode;
  /** Stacked bar under the value. Total card shows the full split; the others show their own share. */
  segments: StatSegment[];
  accent: keyof typeof STAT_ACCENTS;
  loading?: boolean;
}) {
  const a = STAT_ACCENTS[accent];
  return (
    <div className="relative overflow-hidden rounded-xl border bg-card p-5 shadow-sm">
      {/* Soft corner wash so the card reads as a panel, not a plain box */}
      <div className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${a.glow} to-transparent blur-2xl`} />
      <div className="relative space-y-3">
        <div className="flex items-center gap-2">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${a.tile}`}>
            <Icon className="h-4 w-4" />
          </span>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>

        {loading
          ? <Skeleton className="h-9 w-28" />
          : <p className={`text-3xl font-bold leading-none tracking-tight tabular-nums ${a.value}`}>{value.toLocaleString()}</p>}

        <div className="space-y-2">
          <div className="min-h-[16px] text-xs text-muted-foreground">
            {loading ? <Skeleton className="h-3 w-40" /> : sub}
          </div>
          <div className="flex h-1 w-full overflow-hidden rounded-full bg-muted">
            {!loading && segments.map((s, i) => (
              <div
                key={i}
                className={`h-full transition-all duration-500 ${s.className}`}
                style={{ width: `${Math.max(0, Math.min(s.pct, 100))}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_TAB_META: Record<ManualStatusTab, { label: string; dot: string }> = {
  all:          { label: 'All',          dot: 'bg-muted-foreground/40' },
  need_review:  { label: 'Need Review',  dot: 'bg-amber-500' },
  in_progress:  { label: 'In Progress',  dot: 'bg-blue-500' },
  paid:         { label: 'Paid',         dot: 'bg-emerald-500' },
};

/**
 * Segmented status filter above the Manual Submissions / BMC Max tables.
 * Hand-rolled (not the Tabs primitive) so each segment can carry its own count
 * badge and status dot without fighting TabsList's layout.
 */
function StatusSubTabs({ value, onChange, counts }: {
  value: ManualStatusTab;
  onChange: (v: ManualStatusTab) => void;
  counts: Record<ManualStatusTab, number>;
}) {
  return (
    <div className="inline-flex items-center gap-1 rounded-xl border border-border/70 bg-muted/30 p-1 shadow-sm">
      {(Object.keys(STATUS_TAB_META) as ManualStatusTab[]).map(key => {
        const meta = STATUS_TAB_META[key];
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
              active
                ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
                : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
            <span
              className={`ml-0.5 rounded px-1 text-[10px] font-semibold tabular-nums ${
                active ? 'bg-muted text-foreground' : 'bg-muted/70 text-muted-foreground'
              }`}
            >
              {counts[key]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function HybridLedger() {
  const queryClient = useQueryClient();

  // Main ledger tab
  const [ledgerTab, setLedgerTab] = useState<'tap' | 'manual' | 'bmc_max'>('tap');

  // Shared campaign filter + search
  const [campaignFilter, setCampaignFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Status sub-tabs — Manual Submissions and BMC Max each get their own, since a
  // creator can be "in progress" on one campaign type and "paid" on another.
  const [manualStatusTab, setManualStatusTab] = useState<ManualStatusTab>('all');
  const [bmcMaxStatusTab, setBmcMaxStatusTab] = useState<ManualStatusTab>('all');

  // Pagination — 1-based to match DataPagination component
  const [tapPage, setTapPage]         = useState(1);
  const [manualPage, setManualPage]   = useState(1);
  const [bmcMaxPage, setBmcMaxPage]   = useState(1);
  const [pageSize, setPageSize]       = useState(25);

  // Debounce search input by 300 ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset pages to 1 whenever filter/search/pageSize/status-tab changes
  useEffect(() => {
    setTapPage(1);
    setManualPage(1);
    setBmcMaxPage(1);
  }, [debouncedSearch, campaignFilter, pageSize, manualStatusTab, bmcMaxStatusTab]);

  // Pay modal
  const [paySource, setPaySource] = useState<PaySource | null>(null);
  const [txId, setTxId]     = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes]   = useState('');

  // Videos modal
  const [videoCtx, setVideoCtx]         = useState<{ creator_name: string; campaign_id: string; campaign: CampaignMeta | null; manual_row_id?: string } | null>(null);
  const [videoData, setVideoData]       = useState<VideoModalData | null>(null);
  const [videoDataLoading, setVideoDataLoading] = useState(false);
  const [videoModalTab, setVideoModalTab] = useState<'tap' | 'submitted'>('tap');
  const [videoSearch, setVideoSearch]   = useState('');

  // Reject dialog
  const [rejectCtx, setRejectCtx] = useState<{ video_url: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // ── Queries ─────────────────────────────────────────────────────────────────

  /** All campaign options for the filter dropdown — unaffected by search/filter. */
  const { data: campaignOptions } = useQuery({
    queryKey: ['hybrid-campaign-options'],
    queryFn: async () => {
      // Paged — past 1000 rows a bare .select() would drop campaigns off the filter list.
      const [tapCamps, manualCamps] = await Promise.all([
        fetchAllPages<any>((from, to) =>
          supabase.from('hybrid_campaign_progress')
            .select('campaign_id, campaign:campaigns(id, brand_name, product_name)')
            .order('campaign_id', { ascending: true })
            .range(from, to)),
        fetchAllPages<any>((from, to) =>
          supabase.from('hybrid_video_submissions')
            .select('campaign_id, campaign:campaigns(id, brand_name, product_name)')
            .order('campaign_id', { ascending: true })
            .range(from, to)),
      ]);
      const seen = new Set<string>();
      const list: { id: string; label: string }[] = [];
      for (const r of [...tapCamps, ...manualCamps]) {
        if (!seen.has(r.campaign_id)) {
          seen.add(r.campaign_id);
          const c = r.campaign as any;
          list.push({ id: r.campaign_id, label: c ? `${c.brand_name} — ${c.product_name}` : r.campaign_id });
        }
      }
      return list;
    },
  });

  /**
   * Global video totals — intentionally NOT filtered by tab, campaign, or search.
   * The per-tab counts in the summary bar below already cover the filtered view;
   * these three cards are the system-wide picture across every submission type.
   */
  const { data: allSubmissionRows, isLoading: manualTotalsLoading } = useQuery({
    queryKey: ['hybrid-ledger-global-video-totals'],
    queryFn: fetchAllSubmissionVideos,
  });

  const { data: allTapRows, isLoading: tapTotalsLoading } = useQuery({
    queryKey: ['hybrid-ledger-global-tap-videos'],
    queryFn: fetchAllTapVideos,
  });

  const totalsLoading = manualTotalsLoading || tapTotalsLoading;

  const videoTotals = useMemo(() => {
    // ── TAP side ────────────────────────────────────────────────────────────
    // Dedupe on TikTok's video id, which is globally unique — the same video
    // showing up under two campaigns or creators still counts once.
    const tapIds = new Set<string>();
    let tapUnidentified = 0;
    for (const row of allTapRows ?? []) {
      for (const v of row.videos ?? []) {
        if (v.video_id) tapIds.add(v.video_id);
        else tapUnidentified++; // nothing to dedupe against — counts on its own
      }
    }
    const tapUnique = tapIds.size + tapUnidentified;

    // ── Manual side ─────────────────────────────────────────────────────────
    // Review-queue buckets, plus how many of these are already covered by TAP.
    let submitted = 0, approved = 0, pending = 0, rejected = 0;
    let manualOnly = 0, overlap = 0;
    for (const row of allSubmissionRows ?? []) {
      for (const v of row.videos ?? []) {
        // Creator-withdrawn videos are excluded from every card, so the four
        // cards reconcile: approved + pending + rejected == submitted.
        if (isWithdrawn(v)) continue;
        submitted++;
        if (v.status === 'approved') approved++;
        else if (v.status === 'rejected') rejected++;
        // Rows predating the approval workflow carry no status but still need
        // a decision, so anything not explicitly resolved counts as pending.
        else pending++;

        // Submissions whose tiktok_video_id hasn't resolved yet can't be proven
        // to overlap, so they count as their own video — same rule as computeDedupStats.
        if (v.tiktok_video_id && tapIds.has(v.tiktok_video_id)) overlap++;
        else manualOnly++;
      }
    }

    return {
      submitted, approved, pending, rejected,
      tapUnique, manualOnly, overlap,
      uniqueTotal: tapUnique + manualOnly,
    };
  }, [allSubmissionRows, allTapRows]);

  /** Percentage helpers for the card bars/subtext — guard divide-by-zero on an empty ledger. */
  const pctOfSubmitted = (n: number) => (videoTotals.submitted > 0 ? (n / videoTotals.submitted) * 100 : 0);
  const pctOfUnique    = (n: number) => (videoTotals.uniqueTotal > 0 ? (n / videoTotals.uniqueTotal) * 100 : 0);

  /**
   * Index queries — lightweight, ALL matching rows.
   * Used to build cross-tab maps (tapMap/manualMap) and compute accurate stats.
   * Without these, cross-tab dedup breaks when a paired row lives on a different page.
   *
   * Paged via fetchAllPages — a bare .select() stops at PostgREST's 1000-row cap,
   * which would quietly understate every stat and the pagination totals below.
   */
  const { data: tapIndex } = useQuery({
    queryKey: ['hybrid-ledger-tap-index', campaignFilter, debouncedSearch],
    queryFn: () => fetchAllPages<any>((from, to) => {
      let q = supabase
        .from('hybrid_campaign_progress')
        .select('id, campaign_id, creator_id, creator_name, video_count, videos, payment_status, payment_basis, total_gmv, campaign:campaigns(id, brand_name, product_name, min_videos, guaranteed_payment, is_hybrid)')
        .order('total_gmv', { ascending: false })
        .order('id', { ascending: true });
      if (campaignFilter !== 'all') q = q.eq('campaign_id', campaignFilter) as any;
      if (debouncedSearch) q = q.ilike('creator_name', `%${debouncedSearch}%`) as any;
      return q.range(from, to);
    }),
  });

  const { data: manualIndex } = useQuery({
    queryKey: ['hybrid-ledger-manual-index', campaignFilter, debouncedSearch],
    queryFn: () => fetchAllPages<any>((from, to) => {
      let q = supabase
        .from('hybrid_video_submissions')
        .select('id, campaign_id, creator_id, creator_name, video_count, approved_count, videos, payment_status, payment_basis, submission_type, campaign:campaigns(min_videos, max_required_videos)')
        .eq('submission_type', 'hybrid')
        .order('video_count', { ascending: false })
        .order('id', { ascending: true });
      if (campaignFilter !== 'all') q = q.eq('campaign_id', campaignFilter) as any;
      if (debouncedSearch) q = q.ilike('creator_name', `%${debouncedSearch}%`) as any;
      return q.range(from, to);
    }),
  });

  const { data: bmcMaxIndex } = useQuery({
    queryKey: ['hybrid-ledger-bmc-max-index', campaignFilter, debouncedSearch],
    queryFn: () => fetchAllPages<any>((from, to) => {
      let q = supabase
        .from('hybrid_video_submissions')
        .select('id, campaign_id, creator_id, creator_name, video_count, approved_count, videos, payment_status, payment_basis, submission_type, campaign:campaigns(min_videos, max_required_videos)')
        .eq('submission_type', 'bmc_max')
        .order('video_count', { ascending: false })
        .order('id', { ascending: true });
      if (campaignFilter !== 'all') q = q.eq('campaign_id', campaignFilter) as any;
      if (debouncedSearch) q = q.ilike('creator_name', `%${debouncedSearch}%`) as any;
      return q.range(from, to);
    }),
  });

  /** TAP progress — DB-paginated via .range(), full fields for table display. */
  const { data: tapRows, isLoading: tapLoading } = useQuery({
    queryKey: ['hybrid-ledger-tap', campaignFilter, debouncedSearch, tapPage, pageSize],
    queryFn: async () => {
      let q = supabase
        .from('hybrid_campaign_progress')
        .select('*, campaign:campaigns(id, brand_name, product_name, min_videos, guaranteed_payment, is_hybrid)')
        .order('total_gmv', { ascending: false })
        .range((tapPage - 1) * pageSize, tapPage * pageSize - 1);
      if (campaignFilter !== 'all') q = q.eq('campaign_id', campaignFilter) as any;
      if (debouncedSearch) q = q.ilike('creator_name', `%${debouncedSearch}%`) as any;
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ProgressRow[];
    },
  });

  /**
   * Manual submissions (hybrid) + BMC Max — both DB-paginated AND status-bucketed
   * entirely in SQL via the get_hybrid_ledger_page()/get_hybrid_ledger_status_counts()
   * RPCs (see migration 20260909000000_hybrid_ledger_status_rpc.sql). The status
   * bucket (need_review/in_progress/paid) depends on comparing a per-row active-video
   * count against a per-campaign threshold — not expressible as a plain PostgREST
   * column filter — so it used to be computed client-side from a full unpaginated
   * fetch of every matching row, then re-queried with `.in('id', <those ids>)`. That
   * still paginated correctly (the id list was the full bucket, not just one page),
   * but doesn't scale: a large `.in()` list risks PostgREST URL-length limits, and
   * every status-tab switch round-trips every matching row's full `videos` array to
   * the client just to throw it away. Pushing the bucketing into SQL means the
   * client only ever receives the current page.
   */
  const { data: manualStatusCounts } = useQuery({
    queryKey: ['hybrid-ledger-manual', 'status-counts', campaignFilter, debouncedSearch],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_hybrid_ledger_status_counts', {
        p_submission_type: 'hybrid',
        p_campaign_id: campaignFilter !== 'all' ? campaignFilter : null,
        p_search: debouncedSearch || null,
      });
      if (error) throw error;
      return ((data ?? [])[0] ?? EMPTY_STATUS_COUNTS) as ManualStatusCounts;
    },
  });

  const { data: bmcMaxStatusCounts } = useQuery({
    queryKey: ['hybrid-ledger-bmc-max', 'status-counts', campaignFilter, debouncedSearch],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_hybrid_ledger_status_counts', {
        p_submission_type: 'bmc_max',
        p_campaign_id: campaignFilter !== 'all' ? campaignFilter : null,
        p_search: debouncedSearch || null,
      });
      if (error) throw error;
      return ((data ?? [])[0] ?? EMPTY_STATUS_COUNTS) as ManualStatusCounts;
    },
  });

  const { data: manualGroups, isLoading: manualLoading } = useQuery({
    queryKey: ['hybrid-ledger-manual', 'page', campaignFilter, debouncedSearch, manualStatusTab, manualPage, pageSize],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_hybrid_ledger_page', {
        p_submission_type: 'hybrid',
        p_campaign_id: campaignFilter !== 'all' ? campaignFilter : null,
        p_search: debouncedSearch || null,
        p_status_bucket: manualStatusTab,
        p_page: manualPage,
        p_page_size: pageSize,
      });
      if (error) throw error;
      return (data ?? []) as ManualCreatorRow[];
    },
  });

  const { data: bmcMaxGroups, isLoading: bmcMaxLoading } = useQuery({
    queryKey: ['hybrid-ledger-bmc-max', 'page', campaignFilter, debouncedSearch, bmcMaxStatusTab, bmcMaxPage, pageSize],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_hybrid_ledger_page', {
        p_submission_type: 'bmc_max',
        p_campaign_id: campaignFilter !== 'all' ? campaignFilter : null,
        p_search: debouncedSearch || null,
        p_status_bucket: bmcMaxStatusTab,
        p_page: bmcMaxPage,
        p_page_size: pageSize,
      });
      if (error) throw error;
      return (data ?? []) as ManualCreatorRow[];
    },
  });

  // ── Derived state ────────────────────────────────────────────────────────────

  /** Fast lookup: campaign_id:creator_id → TAP index row (all matching, not just current page) */
  const tapMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const r of tapIndex ?? []) {
      if (r.creator_id) map.set(`${r.campaign_id}:${r.creator_id}`, r);
    }
    return map;
  }, [tapIndex]);

  /** Fast lookup: campaign_id:creator_id → manual index row (all matching, not just current page) */
  const manualMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const r of manualIndex ?? []) {
      if (r.creator_id) map.set(`${r.campaign_id}:${r.creator_id}`, r);
    }
    return map;
  }, [manualIndex]);

  const tapStats = useMemo(() => ({
    total: (tapIndex ?? []).length,
    qualified: (tapIndex ?? []).filter(r => r.video_count >= (r.campaign?.min_videos ?? 10)).length,
    unpaidQualified: (tapIndex ?? []).filter(r => r.video_count >= (r.campaign?.min_videos ?? 10) && (r.payment_status ?? 'unpaid') === 'unpaid').length,
    totalGmv: (tapIndex ?? []).reduce((s, r) => s + Number(r.total_gmv ?? 0), 0),
  }), [tapIndex]);

  const manualStats = useMemo(() => {
    const all = manualIndex ?? [];
    const qualified = all.filter(r => (r.approved_count ?? 0) >= (r.campaign?.min_videos ?? 10));
    const unpaidQualified = qualified.filter(r => (r.payment_status ?? 'unpaid') === 'unpaid');
    return {
      total: all.length,
      qualified: qualified.length,
      unpaidQualified: unpaidQualified.length,
      // Counted from the videos array, not the video_count column — that column is
      // jsonb_array_length(videos), so it still counts creator-withdrawn entries.
      totalSubmissions: all.reduce((s, r) => s + liveVideoCount(r), 0),
    };
  }, [manualIndex]);

  const bmcMaxStats = useMemo(() => {
    const all = bmcMaxIndex ?? [];
    const qualified = all.filter((r: any) => {
      const threshold = r.campaign?.max_required_videos ?? 1;
      return (r.approved_count ?? 0) >= threshold;
    });
    const unpaidQualified = qualified.filter((r: any) => (r.payment_status ?? 'unpaid') === 'unpaid');
    return {
      total: all.length,
      qualified: qualified.length,
      unpaidQualified: unpaidQualified.length,
      // Same as above — video_count includes withdrawn entries, the array does not.
      totalSubmissions: all.reduce((s: number, r: any) => s + liveVideoCount(r), 0),
    };
  }, [bmcMaxIndex]);

  /** Row count for the CURRENT status sub-tab (from the RPC counts query) — drives the empty-state check + pagination. */
  const manualCounts = manualStatusCounts ?? EMPTY_STATUS_COUNTS;
  const bmcMaxCounts = bmcMaxStatusCounts ?? EMPTY_STATUS_COUNTS;
  const manualFilteredTotal = manualStatusTab === 'all' ? manualCounts.total : manualCounts[manualStatusTab];
  const bmcMaxFilteredTotal = bmcMaxStatusTab === 'all' ? bmcMaxCounts.total : bmcMaxCounts[bmcMaxStatusTab];

  // ── Unified pay mutation — handles TAP and Manual tabs, syncs both tables ──

  const payMutation = useMutation({
    mutationFn: async (src: PaySource) => {
      const { data: { session } } = await supabase.auth.getSession();
      const amountCents = Math.round(Number(amount) * 100);
      const now = new Date().toISOString();
      const paidBy = session?.user?.email ?? null;

      // ── Server-side eligibility re-check before committing payment ──────────
      // Guards against stale UI state and double-payment race conditions.
      if (src.source === 'tap') {
        // Hybrid campaigns are paid exclusively from the Manual Submissions tab.
        if (src.tapRow.campaign?.is_hybrid) {
          await logUpdate('hybrid_campaign_progress', src.tapRow.id, {}, {},
            { status: 'failed', error: 'hybrid_tap_payout_blocked', description: `TAP payout blocked for hybrid campaign — @${src.tapRow.creator_name} must be paid from Manual tab` });
          throw new Error('Hybrid campaigns are paid from the Manual Submissions tab, not TAP');
        }
        const { data: current, error: fetchErr } = await supabase
          .from('hybrid_campaign_progress')
          .select('payment_status, video_count')
          .eq('id', src.tapRow.id)
          .single();
        if (fetchErr || !current) {
          await logUpdate('hybrid_campaign_progress', src.tapRow.id, {}, {},
            { status: 'failed', error: fetchErr?.message ?? 'Row not found', description: `Eligibility check failed for @${src.tapRow.creator_name}` });
          throw new Error('Could not verify eligibility — please refresh and try again');
        }
        if (current.payment_status === 'paid') {
          await logUpdate('hybrid_campaign_progress', src.tapRow.id, {}, {},
            { status: 'failed', error: 'already_paid', description: `Double-payment blocked for @${src.tapRow.creator_name}` });
          throw new Error('This creator has already been paid');
        }
        const tapRequired = src.tapRow.campaign?.min_videos ?? 10;
        if (current.video_count < tapRequired) {
          await logUpdate('hybrid_campaign_progress', src.tapRow.id, {}, {},
            { status: 'failed', error: 'insufficient_videos', description: `Payment blocked: @${src.tapRow.creator_name} only has ${current.video_count}/${tapRequired} TAP videos` });
          throw new Error(`Creator is not eligible — only ${current.video_count}/${tapRequired} videos`);
        }
      } else {
        // Fetch fresh row including videos to mark approved ones as paid
        const { data: current, error: fetchErr } = await supabase
          .from('hybrid_video_submissions')
          .select('payment_status, approved_count, videos')
          .eq('id', src.manualRow.id)
          .single();
        if (fetchErr || !current) {
          await logUpdate('hybrid_video_submissions', src.manualRow.id, {}, {},
            { status: 'failed', error: fetchErr?.message ?? 'Row not found', description: `Eligibility check failed for @${src.manualRow.creator_name}` });
          throw new Error('Could not verify eligibility — please refresh and try again');
        }
        if (current.payment_status === 'paid') {
          await logUpdate('hybrid_video_submissions', src.manualRow.id, {}, {},
            { status: 'failed', error: 'already_paid', description: `Double-payment blocked for @${src.manualRow.creator_name}` });
          throw new Error('This creator has already been paid');
        }
        const requiredVideos = src.manualRow.campaign?.is_bmc_max
          ? (src.manualRow.campaign?.max_required_videos ?? 1)
          : (src.manualRow.campaign?.min_videos ?? 10);
        if ((current.approved_count ?? 0) < requiredVideos) {
          await logUpdate('hybrid_video_submissions', src.manualRow.id, {}, {},
            { status: 'failed', error: 'insufficient_approved_videos', description: `Payment blocked: @${src.manualRow.creator_name} only has ${current.approved_count ?? 0}/${requiredVideos} approved videos` });
          throw new Error(`Creator is not eligible — only ${current.approved_count ?? 0}/${requiredVideos} videos approved`);
        }

        // Calculate per-video amount: prefer the admin-entered amount, fall back to campaign guaranteed_payment
        const totalAmountCents = amountCents > 0
          ? amountCents
          : (src.manualRow.campaign?.guaranteed_payment != null
              ? Math.round(Number(src.manualRow.campaign.guaranteed_payment) * 100)
              : null);
        const approvedUnpaidCount = (current.videos ?? []).filter(
          (v: ManualVideoEntry) => v.status === 'approved' && !v.is_paid
        ).length;
        // Hybrid: divide by the required threshold — extra approved videos don't dilute the rate.
        // BMC Max / others: divide by actual approved-unpaid count.
        const divideBy = src.manualRow.campaign?.is_hybrid ? requiredVideos : approvedUnpaidCount;
        const perVideoAmountCents = totalAmountCents != null && divideBy > 0
          ? Math.round(totalAmountCents / divideBy)
          : null;

        // Mark every approved video as paid in the same write
        const paidVideos = (current.videos ?? []).map((v: ManualVideoEntry) =>
          v.status === 'approved'
            ? { ...v, is_paid: true, paid_at: now, paid_by: paidBy, per_video_amount_cents: perVideoAmountCents }
            : v
        );
        const manualPayload = {
          payment_status: 'paid' as const,
          payment_basis: src.manualRow.campaign?.is_bmc_max ? 'bmc_max' as const : 'manual' as const,
          payment_transaction_id: txId.trim() || null,
          payment_amount_cents: amountCents || null,
          per_video_amount_cents: perVideoAmountCents,
          payment_notes: notes.trim() || null,
          paid_at: now,
          paid_by: paidBy,
          videos: paidVideos,
        };

        const { error } = await supabase
          .from('hybrid_video_submissions')
          .update(manualPayload)
          .eq('id', src.manualRow.id);
        if (error) {
          await logUpdate('hybrid_video_submissions', src.manualRow.id,
            { payment_status: src.manualRow.payment_status },
            { payment_status: 'paid' },
            { status: 'failed', error: error.message, description: `Failed hybrid payout for @${src.manualRow.creator_name}` });
          throw error;
        }
        await logUpdate('hybrid_video_submissions', src.manualRow.id,
          { payment_status: src.manualRow.payment_status },
          { payment_status: 'paid', approved_videos_marked_paid: current.approved_count },
          { status: 'success', description: `Admin recorded hybrid payout for @${src.manualRow.creator_name} — tx: ${txId.trim() || 'N/A'}, ${current.approved_count} approved videos marked paid` });
        return;
      }

      const progressPayload = {
        payment_status: 'paid' as const,
        payment_basis: 'tap' as const,
        payment_transaction_id: txId.trim() || null,
        payment_amount_cents: amountCents || null,
        payment_notes: notes.trim() || null,
        paid_at: now,
        paid_by: paidBy,
      };

      // ── TAP-only payout (non-hybrid campaigns) ───────────────────────────────
      const { error } = await supabase
        .from('hybrid_campaign_progress')
        .update(progressPayload)
        .eq('id', src.tapRow.id);
      if (error) {
        await logUpdate('hybrid_campaign_progress', src.tapRow.id,
          { payment_status: src.tapRow.payment_status }, progressPayload,
          { status: 'failed', error: error.message, description: `Failed hybrid payout for @${src.tapRow.creator_name} via TAP` });
        throw error;
      }
      await logUpdate('hybrid_campaign_progress', src.tapRow.id,
        { payment_status: src.tapRow.payment_status }, progressPayload,
        { status: 'success', description: `Admin recorded hybrid payout for @${src.tapRow.creator_name} via TAP — tx: ${txId.trim() || 'N/A'}` });
    },
    onSuccess: () => {
      toast.success('Payment recorded');
      closePayModal();
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-tap'] });
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-tap-index'] });
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-manual'] });
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-manual-index'] });
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-bmc-max'] });
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-bmc-max-index'] });
      queryClient.invalidateQueries({ queryKey: ['hybrid-campaign-options'] });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to record payment'),
  });

  // ── Approve / Reject video mutation ──────────────────────────────────────────

  const approveVideoMutation = useMutation({
    mutationFn: async ({ videoUrl, newStatus, rejectionReason }: {
      videoUrl: string;
      newStatus: 'approved' | 'rejected';
      rejectionReason?: string;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const reviewedBy = session?.user?.email ?? null;
      const now = new Date().toISOString();
      const rowId = videoCtx?.manual_row_id;
      if (!rowId) throw new Error('No submission row ID — please close and reopen the modal');

      const { data: current, error: fetchErr } = await supabase
        .from('hybrid_video_submissions')
        .select('videos, approved_count')
        .eq('id', rowId)
        .single();
      if (fetchErr || !current) throw new Error('Could not fetch submission data');

      const updatedVideos: ManualVideoEntry[] = (current.videos ?? []).map((v: ManualVideoEntry) => {
        if (v.video_url !== videoUrl) return v;
        return {
          ...v,
          status: newStatus,
          reviewed_at: now,
          reviewed_by: reviewedBy,
          rejection_reason: newStatus === 'rejected' ? (rejectionReason?.trim() || null) : null,
        };
      });
      const newApprovedCount = updatedVideos.filter(v => v.status === 'approved').length;

      const { error } = await supabase
        .from('hybrid_video_submissions')
        .update({ videos: updatedVideos, approved_count: newApprovedCount })
        .eq('id', rowId);
      if (error) {
        await logUpdate('hybrid_video_submissions', rowId,
          { approved_count: current.approved_count },
          { approved_count: newApprovedCount },
          { status: 'failed', error: error.message, description: `Failed to ${newStatus} video for @${videoCtx?.creator_name}` });
        throw error;
      }
      await logUpdate('hybrid_video_submissions', rowId,
        { approved_count: current.approved_count },
        { approved_count: newApprovedCount },
        { status: 'success', description: `Admin ${newStatus} video for @${videoCtx?.creator_name}: ${videoUrl}` });

      return updatedVideos;
    },
    onSuccess: (updatedVideos) => {
      setVideoData(prev => prev ? { ...prev, manual_subs: updatedVideos } : prev);
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-manual'] });
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-manual-index'] });
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-bmc-max'] });
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-bmc-max-index'] });
      // Approving/rejecting moves a video between the summary-card buckets.
      queryClient.invalidateQueries({ queryKey: ['hybrid-ledger-global-video-totals'] });
      toast.success('Video status updated');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update video status'),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function openPayModal(tapRow: ProgressRow) {
    const manualRow = tapRow.creator_id ? manualMap.get(`${tapRow.campaign_id}:${tapRow.creator_id}`) : undefined;
    setPaySource({ source: 'tap', tapRow, manualRow });
    setTxId('');
    setAmount(tapRow.campaign?.guaranteed_payment ? String(tapRow.campaign.guaranteed_payment) : '');
    setNotes('');
  }

  function openManualPayModal(manualRow: ManualCreatorRow) {
    const tapRow = manualRow.creator_id ? tapMap.get(`${manualRow.campaign_id}:${manualRow.creator_id}`) : undefined;
    setPaySource({ source: 'manual', manualRow, tapRow });
    setTxId('');
    setAmount(manualRow.campaign?.guaranteed_payment ? String(manualRow.campaign.guaranteed_payment) : '');
    setNotes('');
  }

  function closePayModal() {
    setPaySource(null);
    setTxId('');
    setAmount('');
    setNotes('');
  }

  async function openVideosModal(
    creatorName: string,
    campaignId: string,
    campaign: CampaignMeta | null,
    defaultTab: 'tap' | 'submitted',
    /** Pass TAP videos when already loaded (TAP tab click) to skip re-fetch. */
    preloadedTapVideos?: HybridProgressVideo[],
    /** Pass manual videos when already loaded (Manual tab click) to skip re-fetch. */
    preloadedManualVideos?: ManualVideoEntry[],
    /** ID of the hybrid_video_submissions row — needed for approve/reject actions. */
    manualRowId?: string,
  ) {
    setVideoCtx({ creator_name: creatorName, campaign_id: campaignId, campaign, manual_row_id: manualRowId });
    setVideoModalTab(defaultTab);
    setVideoSearch('');
    setVideoData(null);
    setVideoDataLoading(true);
    try {
      const [tapResult, manualResult] = await Promise.all([
        preloadedTapVideos !== undefined
          ? Promise.resolve(preloadedTapVideos)
          : supabase
              .from('hybrid_campaign_progress')
              .select('videos')
              .eq('campaign_id', campaignId)
              .eq('creator_name', creatorName)
              .maybeSingle()
              .then(r => ((r.data as any)?.videos ?? []) as HybridProgressVideo[]),
        preloadedManualVideos !== undefined
          ? Promise.resolve(preloadedManualVideos)
          : supabase
              .from('hybrid_video_submissions')
              .select('videos')
              .eq('campaign_id', campaignId)
              .eq('creator_name', creatorName)
              .maybeSingle()
              .then(r => ((r.data as any)?.videos ?? []) as ManualVideoEntry[]),
      ]);
      setVideoData({ tap_videos: tapResult as HybridProgressVideo[], manual_subs: manualResult as ManualVideoEntry[] });
    } finally {
      setVideoDataLoading(false);
    }
  }

  const canPay = txId.trim().length > 0 && Number(amount) > 0;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── Global totals — same numbers on every tab, unaffected by filters ── */}
      <div className="space-y-2">
        {/* <p className="text-xs text-muted-foreground">
          All campaigns · hybrid, BMC Max &amp; reward submissions · not affected by the filters below
        </p> */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <LedgerStatCard
            icon={Film}
            accent="slate"
            label="Total Videos Submitted"
            value={videoTotals.uniqueTotal}
            loading={totalsLoading}
            sub={
              <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <span className="text-sky-600 dark:text-sky-400">{videoTotals.tapUnique.toLocaleString()} from TAP</span>
                <span className="text-muted-foreground/50">·</span>
                <span className="text-violet-600 dark:text-violet-400">{videoTotals.manualOnly.toLocaleString()} manual-only</span>
                {videoTotals.overlap > 0 && (
                  <>
                    <span className="text-muted-foreground/50">·</span>
                    <span>{videoTotals.overlap.toLocaleString()} duplicate{videoTotals.overlap === 1 ? '' : 's'} merged</span>
                  </>
                )}
              </span>
            }
            segments={[
              { pct: pctOfUnique(videoTotals.tapUnique),  className: 'bg-sky-500' },
              { pct: pctOfUnique(videoTotals.manualOnly), className: 'bg-violet-500' },
            ]}
          />
          <LedgerStatCard
            icon={CheckCircle2}
            accent="emerald"
            label="Approved Videos"
            value={videoTotals.approved}
            loading={totalsLoading}
            sub={
              videoTotals.submitted === 0
                ? 'No creator submissions yet'
                : `${pctOfSubmitted(videoTotals.approved).toFixed(0)}% of ${videoTotals.submitted.toLocaleString()} creator submissions`
            }
            segments={[{ pct: pctOfSubmitted(videoTotals.approved), className: 'bg-emerald-500' }]}
          />
          <LedgerStatCard
            icon={Clock3}
            accent="amber"
            label="Pending Approval"
            value={videoTotals.pending}
            loading={totalsLoading}
            sub={
              videoTotals.pending === 0
                ? 'Everything has been reviewed'
                : `${pctOfSubmitted(videoTotals.pending).toFixed(0)}% of creator submissions await review`
            }
            segments={[{ pct: pctOfSubmitted(videoTotals.pending), className: 'bg-amber-500' }]}
          />
          <LedgerStatCard
            icon={XCircle}
            accent="rose"
            label="Rejected Videos"
            value={videoTotals.rejected}
            loading={totalsLoading}
            sub={
              videoTotals.rejected === 0
                ? 'Nothing rejected'
                : `${pctOfSubmitted(videoTotals.rejected).toFixed(0)}% of creator submissions rejected`
            }
            segments={[{ pct: pctOfSubmitted(videoTotals.rejected), className: 'bg-rose-500' }]}
          />
        </div>
      </div>

      {/* ── Filter + summary bar ───────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        {ledgerTab === 'tap' ? (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-lg px-3 py-2 bg-muted/30">
              <Users className="h-4 w-4" />
              <span><strong>{tapStats.total}</strong> creators</span>
              <span className="text-muted-foreground/60">·</span>
              <span className="text-emerald-600 font-medium">{tapStats.qualified} qualified</span>
              {tapStats.unpaidQualified > 0 && (
                <>
                  <span className="text-muted-foreground/60">·</span>
                  <span className="text-yellow-600 font-medium">{tapStats.unpaidQualified} unpaid</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-lg px-3 py-2 bg-muted/30">
              <TrendingUp className="h-4 w-4" />
              <span>Total GMV <strong>{fmtUsd(tapStats.totalGmv)}</strong></span>
            </div>
          </>
        ) : ledgerTab === 'bmc_max' ? (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-lg px-3 py-2 bg-muted/30">
              <Users className="h-4 w-4" />
              <span><strong>{bmcMaxStats.total}</strong> creators</span>
              <span className="text-muted-foreground/60">·</span>
              <span className="text-emerald-600 font-medium">{bmcMaxStats.qualified} qualified</span>
              {bmcMaxStats.unpaidQualified > 0 && (
                <>
                  <span className="text-muted-foreground/60">·</span>
                  <span className="text-yellow-600 font-medium">{bmcMaxStats.unpaidQualified} unpaid</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-lg px-3 py-2 bg-muted/30">
              <Video className="h-4 w-4 text-purple-500" />
              <span><strong>{bmcMaxStats.totalSubmissions}</strong> videos submitted</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-lg px-3 py-2 bg-muted/30">
              <Users className="h-4 w-4" />
              <span><strong>{manualStats.total}</strong> creators</span>
              <span className="text-muted-foreground/60">·</span>
              <span className="text-emerald-600 font-medium">{manualStats.qualified} qualified</span>
              {manualStats.unpaidQualified > 0 && (
                <>
                  <span className="text-muted-foreground/60">·</span>
                  <span className="text-yellow-600 font-medium">{manualStats.unpaidQualified} unpaid</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-lg px-3 py-2 bg-muted/30">
              <Video className="h-4 w-4" />
              <span><strong>{manualStats.totalSubmissions}</strong> videos submitted</span>
            </div>
          </>
        )}
        <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search creator..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-8 h-9 w-[180px] text-sm"
            />
          </div>
          <PremiumCombobox
            value={campaignFilter}
            onValueChange={setCampaignFilter}
            placeholder="Filter by campaign"
            searchPlaceholder="Search campaigns..."
            emptyText="No campaigns found."
            className="w-[280px]"
            options={[
              { value: 'all', label: 'All campaigns' },
              ...(campaignOptions ?? []).map(c => ({ value: c.id, label: c.label })),
            ]}
          />
        </div>
      </div>

      {/* ── Main ledger tabs ───────────────────────────────────────────────── */}
      <Tabs value={ledgerTab} onValueChange={v => setLedgerTab(v as 'tap' | 'manual' | 'bmc_max')}>
        <TabsList>
          <TabsTrigger value="tap" className="gap-1.5">
            TAP Progress
            {tapRows && <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px] font-medium">{tapStats.total}</span>}
          </TabsTrigger>
          <TabsTrigger value="manual" className="gap-1.5">
            <Video className="h-3.5 w-3.5" />
            Manual Submissions
            {manualGroups && <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px] font-medium">{manualStats.total}</span>}
          </TabsTrigger>
          <TabsTrigger value="bmc_max" className="gap-1.5">
            <Video className="h-3.5 w-3.5 text-purple-500" />
            BMC Max
            {bmcMaxGroups && <span className="bg-purple-100 text-purple-700 rounded px-1.5 py-0.5 text-[10px] font-medium">{bmcMaxStats.total}</span>}
          </TabsTrigger>
        </TabsList>

        {/* ── Tab 1: TAP Progress ─────────────────────────────────────────── */}
        <TabsContent value="tap" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Creator Progress (TAP Data)</CardTitle>
            </CardHeader>
            <CardContent>
              {tapLoading ? (
                <div className="p-6 space-y-2">
                  {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
                </div>
              ) : tapStats.total === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-12">
                  {debouncedSearch ? `No creators matching "${debouncedSearch}"` : 'No TAP progress yet — upload TAP data after running a hybrid campaign.'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-6 pl-4">#</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Creator</TableHead>
                      <TableHead className="text-right">Videos</TableHead>
                      <TableHead className="text-right">Unique</TableHead>
                      <TableHead className="text-right">GMV</TableHead>
                      <TableHead className="text-right">Views</TableHead>
                      <TableHead className="text-right">Items Sold</TableHead>
                      <TableHead className="text-right">Status</TableHead>
                      <TableHead className="text-right">Payment</TableHead>
                      <TableHead className="text-right">Paid At</TableHead>
                      <TableHead className="text-right pr-4">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(tapRows ?? []).map((row, i) => {
                      const rowNum = (tapPage - 1) * pageSize + i + 1;
                      const c = row.campaign;
                      const tapMinVids = c?.min_videos ?? 10;
                      const qualified = row.video_count >= tapMinVids;
                      const pct = Math.min((row.video_count / tapMinVids) * 100, 100);
                      const manualForTap = row.creator_id ? manualMap.get(`${row.campaign_id}:${row.creator_id}`) : undefined;
                      const nonRejectedManualVideos = (manualForTap?.videos ?? []).filter((v: any) => v.status !== 'rejected' && v.status !== 'deleted' && !v.is_deleted);
                      const tapDedup = manualForTap ? computeDedupStats(row.videos ?? [], nonRejectedManualVideos) : null;
                      const tapUniqueTotal = tapDedup ? tapDedup.unique_total : row.video_count;
                      return (
                        <TableRow key={row.id}>
                          <TableCell className="pl-4 text-xs text-muted-foreground">{rowNum}</TableCell>
                          <TableCell>
                            {c ? (
                              <div className="min-w-0">
                                <p className="font-medium text-sm leading-tight">{c.brand_name}</p>
                                <p className="text-xs text-muted-foreground truncate max-w-[180px]">{c.product_name}</p>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground font-mono">{row.campaign_id?.slice(0, 8)}…</span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <a
                              href={`https://www.tiktok.com/@${row.creator_name}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium hover:underline"
                            >
                              @{row.creator_name}
                            </a>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-sm">
                                <span className={qualified ? 'text-emerald-600 font-semibold' : ''}>{row.video_count}</span>
                                <span className="text-muted-foreground text-xs"> / {tapMinVids}</span>
                              </span>
                              <div className="h-1 w-16 rounded-full bg-muted overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${qualified ? 'bg-emerald-500' : 'bg-blue-400'}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-sm">
                                <span className={tapUniqueTotal >= tapMinVids ? 'text-emerald-600 font-semibold' : ''}>{tapUniqueTotal}</span>
                                <span className="text-muted-foreground text-xs"> / {tapMinVids}</span>
                              </span>
                              {manualForTap && (
                                <p className="text-[10px] text-muted-foreground leading-tight whitespace-nowrap">
                                  {row.video_count} TAP · {nonRejectedManualVideos.length} manual
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium text-sm">{fmtUsd(Number(row.total_gmv ?? 0))}</TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">{fmtNum(row.total_views ?? 0)}</TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">{fmtNum(row.total_items_sold ?? 0)}</TableCell>
                          <TableCell className="text-right">
                            {qualified
                              ? <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-xs">Qualified</Badge>
                              : <Badge variant="secondary" className="text-xs">In Progress</Badge>}
                          </TableCell>
                          <TableCell className="text-right">
                            {!qualified ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (row.payment_status ?? 'unpaid') === 'paid' ? (
                              <Badge className="bg-green-100 text-green-800 border border-green-200 text-xs whitespace-nowrap">
                                Paid
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200 text-xs">Unpaid</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                            {row.paid_at ? format(new Date(row.paid_at), 'MMM d, yyyy') : '—'}
                          </TableCell>
                          <TableCell className="text-right pr-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openVideosModal(row.creator_name, row.campaign_id, row.campaign, 'tap', row.videos)}
                              >
                                Videos ({row.videos?.length ?? 0})
                              </Button>
                              {/* {qualified && (row.payment_status ?? 'unpaid') === 'unpaid' && !row.campaign?.is_hybrid && (
                                <Button size="sm" onClick={() => openPayModal(row)}>Pay</Button>
                              )} */}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              <DataPagination
                currentPage={tapPage}
                totalPages={Math.ceil(tapStats.total / pageSize)}
                pageSize={pageSize}
                totalItems={tapStats.total}
                onPageChange={setTapPage}
                onPageSizeChange={size => { setPageSize(size); setTapPage(1); }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 2: Manual Submissions ──────────────────────────────────────── */}
        <TabsContent value="manual" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-base">Creator Self-Reported Videos</CardTitle>
                <StatusSubTabs
                  value={manualStatusTab}
                  onChange={setManualStatusTab}
                  counts={{
                    all: manualCounts.total,
                    need_review: manualCounts.need_review,
                    in_progress: manualCounts.in_progress,
                    paid: manualCounts.paid,
                  }}
                />
              </div>
            </CardHeader>
            <CardContent>
              {manualLoading ? (
                <div className="p-6 space-y-2">
                  {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
                </div>
              ) : manualFilteredTotal === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-12">
                  {debouncedSearch
                    ? `No creators matching "${debouncedSearch}"`
                    : manualStatusTab === 'all'
                      ? 'No manual video submissions yet.'
                      : `No submissions in "${STATUS_TAB_META[manualStatusTab].label}" right now.`}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-6 pl-4">#</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Creator</TableHead>
                      <TableHead className="text-right">
                        <div className="flex flex-col items-end leading-tight">
                          <span>Approved</span>
                          <span className="text-[10px] font-normal text-muted-foreground">submitted</span>
                        </div>
                      </TableHead>
                      <TableHead className="text-right">TAP Videos</TableHead>
                      <TableHead className="text-right">Unique</TableHead>
                      <TableHead className="text-right">Status</TableHead>
                      <TableHead className="text-right">Payment</TableHead>
                      <TableHead className="text-right">Paid At</TableHead>
                      <TableHead className="text-right pr-4">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(manualGroups ?? []).map((row, i) => {
                      const rowNum = (manualPage - 1) * pageSize + i + 1;
                      const c = row.campaign;
                      const videoCount = row.video_count ?? 0;
                      const approvedCount = row.approved_count ?? 0;
                      const rejectedCount = (row.videos ?? []).filter((v: any) => v.status === 'rejected').length;
                      const deletedCount = (row.videos ?? []).filter((v: any) => v.status === 'deleted' || v.is_deleted).length;
                      const activeCount = videoCount - rejectedCount - deletedCount;
                      const minVids = c?.min_videos ?? 10;
                      const qualified = approvedCount >= minVids;
                      const pct = Math.min((approvedCount / minVids) * 100, 100);

                      // Look up the corresponding TAP progress row by creator_id
                      const tapRow = row.creator_id ? tapMap.get(`${row.campaign_id}:${row.creator_id}`) : undefined;
                      const tapVideoCount = tapRow?.video_count ?? 0;
                      const tapQualified = tapVideoCount >= minVids;
                      const tapPct = Math.min((tapVideoCount / minVids) * 100, 100);
                      // Exclude rejected AND deleted from dedup/unique — only active videos count
                      const nonRejectedVideos = (row.videos ?? []).filter((v: any) => v.status !== 'rejected' && v.status !== 'deleted' && !v.is_deleted);
                      const manualDedup = tapRow ? computeDedupStats(tapRow.videos ?? [], nonRejectedVideos) : null;
                      const uniqueTotal = manualDedup ? manualDedup.unique_total : nonRejectedVideos.length;

                      return (
                        <TableRow key={row.id}>
                          <TableCell className="pl-4 text-xs text-muted-foreground">{rowNum}</TableCell>

                          {/* Campaign */}
                          <TableCell>
                            {c ? (
                              <div className="min-w-0">
                                <p className="font-medium text-sm leading-tight">{c.brand_name}</p>
                                <p className="text-xs text-muted-foreground truncate max-w-[180px]">{c.product_name}</p>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground font-mono">{row.campaign_id?.slice(0, 8)}…</span>
                            )}
                          </TableCell>

                          {/* Creator */}
                          <TableCell className="whitespace-nowrap">
                            <a
                              href={`https://www.tiktok.com/@${row.creator_name}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium hover:underline"
                            >
                              @{row.creator_name}
                            </a>
                          </TableCell>

                          {/* Manual submitted + approved count */}
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end gap-0.5">
                              <div className="flex items-baseline gap-1">
                                <span className={`text-sm font-semibold tabular-nums ${qualified ? 'text-emerald-600' : ''}`}>{approvedCount}</span>
                                <span className="text-muted-foreground text-xs">/ {activeCount}</span>
                              </div>
                              <div className="h-1 w-16 rounded-full bg-muted overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${qualified ? 'bg-emerald-500' : 'bg-blue-400'}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </TableCell>

                          {/* TAP video count — from hybrid_campaign_progress via tapMap */}
                          <TableCell className="text-right">
                            {tapRow ? (
                              <div className="flex flex-col items-end gap-1">
                                <span className="text-sm">
                                  <span className={tapQualified ? 'text-emerald-600 font-semibold' : ''}>{tapVideoCount}</span>
                                  <span className="text-muted-foreground text-xs"> / 10</span>
                                </span>
                                <div className="h-1 w-16 rounded-full bg-muted overflow-hidden">
                                  <div className={`h-full rounded-full transition-all ${tapQualified ? 'bg-emerald-500' : 'bg-orange-400'}`} style={{ width: `${tapPct}%` }} />
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">No TAP data</span>
                            )}
                          </TableCell>

                          {/* Unique combined count */}
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-sm">
                                <span className={uniqueTotal >= minVids ? 'text-emerald-600 font-semibold' : ''}>{uniqueTotal}</span>
                                <span className="text-muted-foreground text-xs"> / {minVids}</span>
                              </span>
                              {tapRow && (
                                <p className="text-[10px] text-muted-foreground leading-tight whitespace-nowrap">
                                  {tapVideoCount} TAP · {activeCount} manual{deletedCount > 0 ? ` (${deletedCount} del)` : ''}
                                </p>
                              )}
                            </div>
                          </TableCell>

                          {/* Overall status */}
                          <TableCell className="text-right">
                            {qualified
                              ? <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-xs">Qualified</Badge>
                              : <Badge variant="secondary" className="text-xs">In Progress</Badge>}
                          </TableCell>

                          {/* Payment — tracked in hybrid_video_submissions.payment_status */}
                          <TableCell className="text-right">
                            {!qualified ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (row.payment_status ?? 'unpaid') === 'paid' ? (
                              <Badge className="bg-green-100 text-green-800 border border-green-200 text-xs whitespace-nowrap">
                                Paid
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200 text-xs">Unpaid</Badge>
                            )}
                          </TableCell>

                          {/* Paid At */}
                          <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                            {row.paid_at ? format(new Date(row.paid_at), 'MMM d, yyyy') : '—'}
                          </TableCell>

                          {/* Actions */}
                          <TableCell className="text-right pr-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openVideosModal(row.creator_name, row.campaign_id, row.campaign, 'submitted', undefined, row.videos ?? [], row.id)}
                              >
                                <Link2 className="h-3 w-3 mr-1" />
                                Videos ({activeCount})
                              </Button>
                              {qualified && (row.payment_status ?? 'unpaid') === 'unpaid' && (
                                <Button size="sm" onClick={() => openManualPayModal(row)}>Pay</Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              <DataPagination
                currentPage={manualPage}
                totalPages={Math.ceil(manualFilteredTotal / pageSize)}
                pageSize={pageSize}
                totalItems={manualFilteredTotal}
                onPageChange={setManualPage}
                onPageSizeChange={size => { setPageSize(size); setManualPage(1); }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 3: BMC Max Submissions ──────────────────────────────────── */}
        <TabsContent value="bmc_max" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-purple-500" />
                  BMC Max Video Submissions
                </CardTitle>
                <StatusSubTabs
                  value={bmcMaxStatusTab}
                  onChange={setBmcMaxStatusTab}
                  counts={{
                    all: bmcMaxCounts.total,
                    need_review: bmcMaxCounts.need_review,
                    in_progress: bmcMaxCounts.in_progress,
                    paid: bmcMaxCounts.paid,
                  }}
                />
              </div>
            </CardHeader>
            <CardContent>
              {bmcMaxLoading ? (
                <div className="p-6 space-y-2">
                  {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
                </div>
              ) : bmcMaxFilteredTotal === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-12">
                  {debouncedSearch
                    ? `No creators matching "${debouncedSearch}"`
                    : bmcMaxStatusTab === 'all'
                      ? 'No BMC Max video submissions yet.'
                      : `No submissions in "${STATUS_TAB_META[bmcMaxStatusTab].label}" right now.`}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-6 pl-4">#</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead>Creator</TableHead>
                      <TableHead className="text-right">
                        <div className="flex flex-col items-end leading-tight">
                          <span>Approved</span>
                          <span className="text-[10px] font-normal text-muted-foreground">submitted</span>
                        </div>
                      </TableHead>
                      <TableHead className="text-right">Required</TableHead>
                      <TableHead className="text-right">Status</TableHead>
                      <TableHead className="text-right">Payment</TableHead>
                      <TableHead className="text-right">Paid At</TableHead>
                      <TableHead className="text-right pr-4">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(bmcMaxGroups ?? []).map((row, i) => {
                      const rowNum = (bmcMaxPage - 1) * pageSize + i + 1;
                      const c = row.campaign;
                      const required = c?.max_required_videos ?? 1;
                      const videoCount = row.video_count ?? 0;
                      const approvedCount = row.approved_count ?? 0;
                      const rejectedCount = (row.videos ?? []).filter((v: any) => v.status === 'rejected').length;
                      const deletedCount = (row.videos ?? []).filter((v: any) => v.status === 'deleted' || v.is_deleted).length;
                      const activeCount = videoCount - rejectedCount - deletedCount;
                      const qualified = approvedCount >= required;
                      const pct = Math.min((approvedCount / required) * 100, 100);
                      return (
                        <TableRow key={row.id}>
                          <TableCell className="pl-4 text-xs text-muted-foreground">{rowNum}</TableCell>

                          {/* Campaign */}
                          <TableCell>
                            {c ? (
                              <div className="min-w-0">
                                <p className="font-medium text-sm leading-tight">{c.brand_name}</p>
                                <p className="text-xs text-muted-foreground truncate max-w-[180px]">{c.product_name}</p>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground font-mono">{row.campaign_id?.slice(0, 8)}…</span>
                            )}
                          </TableCell>

                          {/* Creator */}
                          <TableCell className="whitespace-nowrap">
                            <a
                              href={`https://www.tiktok.com/@${row.creator_name}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium hover:underline"
                            >
                              @{row.creator_name}
                            </a>
                          </TableCell>

                          {/* Approved / active count */}
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end gap-0.5">
                              <div className="flex items-baseline gap-1">
                                <span className={`text-sm font-semibold tabular-nums ${qualified ? 'text-emerald-600' : 'text-purple-600'}`}>{approvedCount}</span>
                                <span className="text-muted-foreground text-xs">/ {activeCount}</span>
                              </div>
                              <div className="h-1 w-16 rounded-full bg-muted overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${qualified ? 'bg-emerald-500' : 'bg-purple-400'}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </TableCell>

                          {/* Required threshold */}
                          <TableCell className="text-right">
                            <span className="text-sm text-muted-foreground">{required} videos</span>
                          </TableCell>

                          {/* Status */}
                          <TableCell className="text-right">
                            {qualified
                              ? <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-xs">Qualified</Badge>
                              : <Badge variant="secondary" className="text-xs">In Progress</Badge>}
                          </TableCell>

                          {/* Payment */}
                          <TableCell className="text-right">
                            {!qualified ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (row.payment_status ?? 'unpaid') === 'paid' ? (
                              <Badge className="bg-green-100 text-green-800 border border-green-200 text-xs whitespace-nowrap">Paid</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200 text-xs">Unpaid</Badge>
                            )}
                          </TableCell>

                          {/* Paid At */}
                          <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                            {row.paid_at ? format(new Date(row.paid_at), 'MMM d, yyyy') : '—'}
                          </TableCell>

                          {/* Actions */}
                          <TableCell className="text-right pr-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openVideosModal(row.creator_name, row.campaign_id, row.campaign, 'submitted', undefined, row.videos ?? [], row.id)}
                              >
                                <Link2 className="h-3 w-3 mr-1" />
                                Videos ({activeCount})
                              </Button>
                              {qualified && (row.payment_status ?? 'unpaid') === 'unpaid' && (
                                <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => openManualPayModal(row)}>Pay</Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              <DataPagination
                currentPage={bmcMaxPage}
                totalPages={Math.ceil(bmcMaxFilteredTotal / pageSize)}
                pageSize={pageSize}
                totalItems={bmcMaxFilteredTotal}
                onPageChange={setBmcMaxPage}
                onPageSizeChange={size => { setPageSize(size); setBmcMaxPage(1); }}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Videos detail modal (shared, on-demand) ────────────────────────── */}
      {(() => {
        const tapVideos       = videoData?.tap_videos ?? [];
        const manualSubs      = videoData?.manual_subs ?? [];
        const activeManualSubs = manualSubs.filter(s => !s.is_deleted && s.status !== 'deleted');
        const deletedManualCount = manualSubs.length - activeManualSubs.length;
        const dedup           = computeDedupStats(tapVideos, activeManualSubs);
        const q            = videoSearch.trim().toLowerCase();
        const visibleTap   = q
          ? tapVideos.filter(v => (v.video_name ?? '').toLowerCase().includes(q) || v.video_id.toLowerCase().includes(q))
          : tapVideos;
        const visibleManual = q
          ? manualSubs.filter(s => s.video_url.toLowerCase().includes(q))
          : manualSubs;
        const totalGmvV    = tapVideos.reduce((s, v) => s + Number(v.gmv ?? 0), 0);
        const totalViewsV  = tapVideos.reduce((s, v) => s + Number(v.video_views ?? 0), 0);
        const totalItemsV  = tapVideos.reduce((s, v) => s + Number(v.items_sold ?? 0), 0);

        return (
          <Dialog open={!!videoCtx} onOpenChange={(open) => { if (!open) { setVideoCtx(null); setVideoData(null); setVideoSearch(''); } }}>
            <DialogContent className="sm:max-w-4xl max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden">
              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b bg-muted/20 shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                      {videoCtx?.campaign?.brand_name}
                    </p>
                    <h2 className="text-lg font-semibold leading-tight">
                      {videoCtx?.campaign?.product_name ?? 'Product'}
                    </h2>
                    <a
                      href={`https://www.tiktok.com/@${videoCtx?.creator_name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mt-0.5"
                    >
                      @{videoCtx?.creator_name}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  {/* Stat chips — only shown once data is loaded */}
                  {videoData && (() => {
                    const isBmcMax = videoCtx?.campaign?.is_bmc_max;
                    const threshold = isBmcMax
                      ? (videoCtx?.campaign?.max_required_videos ?? 10)
                      : (videoCtx?.campaign?.min_videos ?? 10);
                    const approvedCount = activeManualSubs.filter(s => s.status === 'approved').length;
                    return (
                      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                        {!isBmcMax && (
                          <>
                            <div className="text-center px-3 py-1.5 rounded-lg bg-background border">
                              <p className="text-xs text-muted-foreground">Unique Total</p>
                              <p className="text-sm font-semibold">{dedup.unique_total} / {videoCtx?.campaign?.min_videos ?? 10}</p>
                            </div>
                            <div className="text-center px-3 py-1.5 rounded-lg bg-background border">
                              <p className="text-xs text-muted-foreground">TAP</p>
                              <p className="text-sm font-semibold">{dedup.tap_count}</p>
                            </div>
                          </>
                        )}
                        <div className="text-center px-3 py-1.5 rounded-lg bg-background border">
                          <p className="text-xs text-muted-foreground">{isBmcMax ? 'Submitted' : 'Manual'}</p>
                          <p className="text-sm font-semibold text-blue-600">
                            {activeManualSubs.length}
                            {deletedManualCount > 0 && <span className="text-[10px] font-normal text-muted-foreground ml-1">+{deletedManualCount} del</span>}
                          </p>
                        </div>
                        {activeManualSubs.length > 0 && (
                          <div className="text-center px-3 py-1.5 rounded-lg bg-background border">
                            <p className="text-xs text-muted-foreground">Approved</p>
                            <p className={`text-sm font-semibold ${approvedCount >= threshold ? 'text-emerald-600' : 'text-foreground'}`}>
                              {approvedCount} / {threshold}
                            </p>
                          </div>
                        )}
                        {!isBmcMax && tapVideos.length > 0 && (
                          <>
                            <div className="text-center px-3 py-1.5 rounded-lg bg-background border">
                              <p className="text-xs text-muted-foreground">GMV</p>
                              <p className="text-sm font-semibold text-emerald-600">{fmtUsd(totalGmvV)}</p>
                            </div>
                            <div className="text-center px-3 py-1.5 rounded-lg bg-background border">
                              <p className="text-xs text-muted-foreground">Views</p>
                              <p className="text-sm font-semibold">{fmtNum(totalViewsV)}</p>
                            </div>
                            <div className="text-center px-3 py-1.5 rounded-lg bg-background border">
                              <p className="text-xs text-muted-foreground">Items</p>
                              <p className="text-sm font-semibold">{fmtNum(totalItemsV)}</p>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Loading state */}
              {videoDataLoading ? (
                <div className="p-6 space-y-2 flex-1">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
                </div>
              ) : (
                <Tabs value={videoModalTab} onValueChange={v => { setVideoModalTab(v as 'tap' | 'submitted'); setVideoSearch(''); }} className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <div className="px-6 pt-3 border-b shrink-0">
                    <TabsList className="h-8">
                      {!videoCtx?.campaign?.is_bmc_max && (
                        <TabsTrigger value="tap" className="text-xs gap-1.5">
                          TAP Upload Videos
                          <span className="bg-muted text-muted-foreground rounded px-1 py-0.5 text-[10px] font-medium">{tapVideos.length}</span>
                        </TabsTrigger>
                      )}
                      <TabsTrigger value="submitted" className="text-xs gap-1.5">
                        <Video className="h-3 w-3" />
                        Submitted by Creator
                        <span className="bg-muted text-muted-foreground rounded px-1 py-0.5 text-[10px] font-medium">
                          {activeManualSubs.length}{deletedManualCount > 0 ? ` +${deletedManualCount}d` : ''}
                        </span>
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  {/* TAP videos */}
                  <TabsContent value="tap" className="flex flex-col flex-1 min-h-0 overflow-hidden mt-0">
                    <div className="px-6 py-3 shrink-0">
                      <Input
                        placeholder="Search by video name or ID..."
                        value={videoSearch}
                        onChange={e => setVideoSearch(e.target.value)}
                        className="h-8 text-sm max-w-sm"
                      />
                    </div>
                    {/* Height/flex sizing goes on the Table's own scroll
                        wrapper; a nested overflow div would strand the
                        horizontal scrollbar below the clipped rows. */}
                    <Table containerClassName="flex-1 min-h-0 px-2">
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="w-8 pl-4">#</TableHead>
                            <TableHead>Video</TableHead>
                            <TableHead className="text-right">GMV</TableHead>
                            <TableHead className="text-right">Views</TableHead>
                            <TableHead className="text-right">Items Sold</TableHead>
                            <TableHead className="text-right">Posted</TableHead>
                            <TableHead className="text-right pr-4">Link</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visibleTap.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">
                                {q ? `No videos matching "${videoSearch}"` : 'No TAP videos recorded yet.'}
                              </TableCell>
                            </TableRow>
                          ) : visibleTap.map((v, i) => (
                            <TableRow key={v.video_id} className="group">
                              <TableCell className="pl-4 text-xs text-muted-foreground w-8">{i + 1}</TableCell>
                              <TableCell className="py-3">
                                <p className="text-sm font-medium leading-tight max-w-[280px] truncate">
                                  {v.video_name || <span className="italic text-muted-foreground">Untitled</span>}
                                </p>
                                <p className="text-xs text-muted-foreground font-mono mt-0.5">{v.video_id}</p>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="text-sm font-semibold text-emerald-600">{fmtUsd(Number(v.gmv ?? 0))}</span>
                              </TableCell>
                              <TableCell className="text-right text-sm text-muted-foreground">{fmtNum(v.video_views ?? 0)}</TableCell>
                              <TableCell className="text-right text-sm text-muted-foreground">{fmtNum(v.items_sold ?? 0)}</TableCell>
                              <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                                {v.post_time ? format(new Date(v.post_time), 'MMM d, yyyy') : '—'}
                              </TableCell>
                              <TableCell className="text-right pr-4">
                                <a
                                  href={`https://www.tiktok.com/@${videoCtx?.creator_name}/video/${v.video_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  Watch <ExternalLink className="h-3 w-3" />
                                </a>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                    </Table>
                    {q && (
                      <div className="px-6 py-2 border-t shrink-0 text-xs text-muted-foreground">
                        Showing {visibleTap.length} of {tapVideos.length} videos
                      </div>
                    )}
                  </TabsContent>

                  {/* Manual submissions */}
                  <TabsContent value="submitted" className="flex flex-col flex-1 min-h-0 overflow-hidden mt-0">
                    <div className="px-6 py-3 shrink-0">
                      <Input
                        placeholder="Search by video URL..."
                        value={videoSearch}
                        onChange={e => setVideoSearch(e.target.value)}
                        className="h-8 text-sm max-w-sm"
                      />
                    </div>
                    {/* See the note on the sibling table above. */}
                    <Table containerClassName="flex-1 min-h-0 px-2">
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="w-8 pl-4">#</TableHead>
                            <TableHead>Video URL</TableHead>
                            <TableHead>Web URL</TableHead>
                            <TableHead className="text-right">Status</TableHead>
                            {!videoCtx?.campaign?.is_bmc_max && (
                              <TableHead className="text-right">TAP Match</TableHead>
                            )}
                            <TableHead className="text-right">Submitted</TableHead>
                            <TableHead className="text-right pr-4">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visibleManual.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={videoCtx?.campaign?.is_bmc_max ? 6 : 7} className="text-center text-sm text-muted-foreground py-10">
                                Creator hasn't submitted any videos manually yet.
                              </TableCell>
                            </TableRow>
                          ) : (() => {
                            const qSub = videoSearch.trim().toLowerCase();
                            const visibleSubs = qSub
                              ? manualSubs.filter(s => s.video_url.toLowerCase().includes(qSub))
                              : manualSubs;
                            return visibleSubs.map((s, i) => {
                              const inTap = !!(s.tiktok_video_id && dedup.tapIds.has(s.tiktok_video_id));
                              const videoStatus = s.status ?? 'pending';
                              const isApproving = approveVideoMutation.isPending;
                              const webUrl = buildTiktokWebUrl(videoCtx?.creator_name, s.tiktok_video_id);
                              return (
                                <TableRow key={s.video_url}>
                                  <TableCell className="pl-4 text-xs text-muted-foreground w-8">{i + 1}</TableCell>
                                  <TableCell className="py-3 max-w-[220px]">
                                    <p className={`text-sm font-mono truncate ${s.is_deleted ? 'text-muted-foreground/50 line-through' : 'text-muted-foreground'}`}>{s.video_url}</p>
                                    {s.rejection_reason && (
                                      <TooltipProvider delayDuration={200}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <p className="text-xs text-red-500 mt-0.5 truncate max-w-[200px] cursor-help">Rejected: {s.rejection_reason}</p>
                                          </TooltipTrigger>
                                          <TooltipContent side="bottom" className="max-w-[320px] whitespace-normal break-words">
                                            {s.rejection_reason}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {s.deleted_reason && (
                                      <TooltipProvider delayDuration={200}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <p className="text-xs text-orange-500 mt-0.5 truncate max-w-[200px] cursor-help">Deleted by creator: {s.deleted_reason}</p>
                                          </TooltipTrigger>
                                          <TooltipContent side="bottom" className="max-w-[320px] whitespace-normal break-words">
                                            {s.deleted_reason}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </TableCell>
                                  <TableCell className="py-3 max-w-[220px]">
                                    {webUrl ? (
                                      <a
                                        href={webUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={webUrl}
                                        className={`text-sm font-mono truncate block ${
                                          s.is_deleted
                                            ? 'text-muted-foreground/50 line-through'
                                            : 'text-blue-600 hover:text-blue-700 hover:underline'
                                        }`}
                                      >
                                        {webUrl}
                                      </a>
                                    ) : (
                                      <span className="text-sm text-muted-foreground">—</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {videoStatus === 'approved' ? (
                                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs">Approved</Badge>
                                    ) : videoStatus === 'rejected' ? (
                                      <Badge className="bg-red-100 text-red-800 border border-red-200 text-xs">Rejected</Badge>
                                    ) : (videoStatus === 'deleted' || s.is_deleted) ? (
                                      <Badge className="bg-orange-100 text-orange-700 border border-orange-200 text-xs">Deleted</Badge>
                                    ) : (
                                      <Badge variant="secondary" className="text-xs">Pending</Badge>
                                    )}
                                  </TableCell>
                                  {!videoCtx?.campaign?.is_bmc_max && (
                                    <TableCell className="text-right text-sm">
                                      {!s.tiktok_video_id ? (
                                        <span className="text-muted-foreground">—</span>
                                      ) : inTap ? (
                                        <span className="text-emerald-600 font-medium">Yes</span>
                                      ) : (
                                        <span className="text-muted-foreground">No</span>
                                      )}
                                    </TableCell>
                                  )}
                                  <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                                    {format(new Date(s.submitted_at), 'MMM d, yyyy')}
                                  </TableCell>
                                  <TableCell className="text-right pr-4">
                                    <div className="flex items-center justify-end gap-1.5">
                                      <a
                                        href={webUrl || s.video_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline mr-1"
                                      >
                                        Watch <ExternalLink className="h-3 w-3" />
                                      </a>
                                      {videoStatus === 'pending' && !s.is_deleted && (
                                        <>
                                          <Button
                                            size="sm"
                                            className="h-6 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                            disabled={isApproving || !videoCtx?.manual_row_id}
                                            onClick={() => approveVideoMutation.mutate({ videoUrl: s.video_url, newStatus: 'approved' })}
                                          >
                                            Approve
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 px-2 text-xs border-red-200 text-red-600 hover:bg-red-50"
                                            disabled={isApproving || !videoCtx?.manual_row_id}
                                            onClick={() => { setRejectCtx({ video_url: s.video_url }); setRejectReason(''); }}
                                          >
                                            Reject
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            });
                          })()}
                        </TableBody>
                    </Table>
                    {/* Footer: dedup legend */}
                    {tapVideos.length > 0 && manualSubs.length > 0 && (
                      <div className="px-6 py-2 border-t shrink-0 text-xs text-muted-foreground">
                        <span className="font-medium text-emerald-600">Yes</span> — counted in TAP upload.
                        {'  ·  '}
                        <span className="font-medium">No</span> — not in TAP; adds to unique count.
                        {'  ·  '}
                        <span className="font-medium">—</span> — TikTok ID not resolved yet.
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* ── Reject video dialog ──────────────────────────────────────────────── */}
      <Dialog open={!!rejectCtx} onOpenChange={(open) => { if (!open) { setRejectCtx(null); setRejectReason(''); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Reject Video</DialogTitle>
            <DialogDescription>
              Optionally add a reason — shown to the admin in the video list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="reject-reason">Reason (optional)</Label>
            <Textarea
              id="reject-reason"
              placeholder="e.g. Video doesn't match campaign brief"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectCtx(null); setRejectReason(''); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={approveVideoMutation.isPending}
              onClick={() => {
                if (!rejectCtx) return;
                approveVideoMutation.mutate(
                  { videoUrl: rejectCtx.video_url, newStatus: 'rejected', rejectionReason: rejectReason },
                  { onSuccess: () => { setRejectCtx(null); setRejectReason(''); } },
                );
              }}
            >
              {approveVideoMutation.isPending ? 'Saving...' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Pay modal ─────────────────────────────────────────────────────────── */}
      {(() => {
        const src = paySource;
        const creatorName = src?.source === 'tap' ? src.tapRow.creator_name : src?.manualRow.creator_name;
        const campaign    = src?.source === 'tap' ? src.tapRow.campaign    : src?.manualRow.campaign;
        const videoCount  = src?.source === 'tap' ? src.tapRow.video_count : src?.manualRow.approved_count ?? 0;
        const isBmcMax    = src?.source !== 'tap' && !!(src?.manualRow?.campaign?.is_bmc_max);
        const requiredVids = isBmcMax
          ? (campaign?.max_required_videos ?? 10)
          : (campaign?.min_videos ?? 10);
        const basisLabel  = src?.source === 'tap' ? 'TAP Upload' : isBmcMax ? 'BMC Max' : 'Manual Submissions';
        return (
          <Dialog open={!!paySource} onOpenChange={(open) => { if (!open) closePayModal(); }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{isBmcMax ? 'Record BMC Max Payout' : 'Record Hybrid Payout'}</DialogTitle>
                <DialogDescription className="space-y-0.5 pt-1">
                  <span className="block font-medium text-foreground">@{creatorName}</span>
                  <span className="block text-xs">
                    {campaign?.brand_name} · {campaign?.product_name}
                  </span>
                  <span className="block text-xs">
                    Basis: <strong>{basisLabel}</strong>
                    {' · '}
                    Videos: <strong>{videoCount} / {requiredVids}</strong>
                  </span>
                  {campaign?.guaranteed_payment != null && (
                    <span className="block text-xs">
                      Guaranteed payout: <strong>${campaign.guaranteed_payment.toLocaleString()}</strong>
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label htmlFor="tx-id">Transaction ID <span className="text-destructive">*</span></Label>
                  <Input
                    id="tx-id"
                    placeholder="e.g. TXN-2026-0001"
                    value={txId}
                    onChange={e => setTxId(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pay-amount">Amount ($) <span className="text-destructive">*</span></Label>
                  <Input
                    id="pay-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pay-notes">Notes</Label>
                  <Textarea
                    id="pay-notes"
                    placeholder="Optional notes..."
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closePayModal} disabled={payMutation.isPending}>Cancel</Button>
                <Button
                  onClick={() => src && payMutation.mutate(src)}
                  disabled={payMutation.isPending || !canPay}
                >
                  {payMutation.isPending ? 'Saving...' : 'Mark as Paid'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}
